// POC: Age / legal-drinking-age consent gate shown before the curtain raiser. Menu + curtain
// APIs are prefetched optimistically with consentLDA=true (see useMenuDataPrefetch — most users
// agree), so Agree reveals instantly with no lag. Decline is the wrong-guess path: it goes
// through refetchWithConsent behind a loader before revealing.
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontWeight } from "@swiggy-private/dls-web-common/dist/components/types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { AnalyticsContext, TableContext, useDigitalMenuConfig } from "@swiggy-private/portal-dineout-context";
import { sendAgeConsentAgreeEvent, sendAgeConsentCancelEvent, sendAgeConsentScreenViewEvent } from "./helpers";
import {
    AgeCircle,
    AgeGateContainer,
    AgeNumber,
    AgreeButton,
    CancelButton,
    Divider,
    Heading,
    HeadingTop,
    LegalText,
    Spacer,
} from "./styled";
import { AgeGateProps } from "./types";
import { useMenuPrefetch } from "../../context/MenuPrefetchContext";
import { setDrinkingAgeConsent } from "../../utils/drinkingAgeConsent";
import { CurtainOverlayLoader } from "../Menu/CurtainOverlayLoader";

const AgeGate = ({ config, onResolved }: AgeGateProps) => {
    const { tableId = "", restaurantId = "" } = useContext(TableContext);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    // Full menu config carries `version` (isV2Config) — the consent config alone does not.
    const { config: menuConfig } = useDigitalMenuConfig();
    const { refetchWithConsent } = useMenuPrefetch();
    const [isRefetching, setIsRefetching] = useState(false);

    const analyticsParams = {
        restaurantId,
        tableId,
        ldaAgeThreshold: config.legalDrinkingAge,
        config: menuConfig,
    };

    // screen-view fires exactly once when the consent page renders. Consent persists only for
    // this dining session, so a re-scan in the same session does not re-mount the gate.
    const hasSentScreenViewRef = useRef(false);
    useEffect(() => {
        if (hasSentScreenViewRef.current) return;
        hasSentScreenViewRef.current = true;
        sendAgeConsentScreenViewEvent({ eventFn: impressionEvent, ...analyticsParams });
        // Fire once on mount; analyticsParams is derived from mount-stable values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAgree = useCallback(() => {
        // Optimistic prefetch already ran with consentLDA=true — data is ready, reveal now.
        sendAgeConsentAgreeEvent({ eventFn: clickEvent, ...analyticsParams });
        setDrinkingAgeConsent(tableId, true);
        onResolved(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, onResolved, clickEvent, restaurantId, menuConfig, config.legalDrinkingAge]);

    const handleDecline = useCallback(async () => {
        sendAgeConsentCancelEvent({ eventFn: clickEvent, ...analyticsParams });
        setDrinkingAgeConsent(tableId, false);
        setIsRefetching(true);
        try {
            // Reveal only once the consent=false data has actually landed.
            await refetchWithConsent(false);
            onResolved(false);
        } catch {
            // Failed — keep the gate mounted so the user can retry instead of revealing stale data.
            setIsRefetching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, refetchWithConsent, onResolved, clickEvent, restaurantId, menuConfig, config.legalDrinkingAge]);

    if (isRefetching) {
        return <CurtainOverlayLoader fixed />;
    }

    return (
        <AgeGateContainer>
            <HeadingTop variant="V2.hero.neutral.bold" color="text_Highest_Emphasis">
                {config.titleText}
            </HeadingTop>
            <AgeCircle>
                <AgeNumber variant="V2.hero.neutral.bold" color="text_Highest_Emphasis" fontWeight={FontWeight.medium}>
                    {config.legalDrinkingAge}
                </AgeNumber>
            </AgeCircle>
            <Heading variant="V2.hero.neutral.bold" color="text_Highest_Emphasis">
                {config.subtitleText}
            </Heading>
            <Divider />
            <LegalText variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                {config.consentText}
            </LegalText>
            <Spacer />
            <AgreeButton onClick={handleAgree}>
                <Text variant="V2.subheading1.neutral.bold" color="text_inverse_Highest_Emphasis">
                    {config.primaryButtonText}
                </Text>
            </AgreeButton>
            <CancelButton onClick={handleDecline}>
                <Text variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                    {config.secondaryButtonText}
                </Text>
            </CancelButton>
        </AgeGateContainer>
    );
};

export default memo(AgeGate);
